import { Component, HostListener, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ProfileService } from 'library-explorer';
import { ScormStatus } from '@app/model/enums/scorm-status.enum';
import { Subject } from 'rxjs';
import { finalize, mergeMap, takeUntil } from 'rxjs/operators';
import { ScormPackageResults } from 'src/app/model/scrom-package-data';
import { DownloadService } from 'src/app/services/api/download.service';
import { FileModel, FullScreenBehaviourType } from 'library-explorer';
import { ScormApiService } from 'src/app/services/scorm-api.service';
import { IframeFullscreenComponent } from '../iframe-fullscreen/iframe-fullscreen.component';
import { SettingsService } from '@app/services/settings.service';
import { MediaPresignApiService, FieldName, EntityTypeId, BundleType } from 'library-explorer';

@Component({
    selector: 'app-slide-scorm-package',
    templateUrl: './slide-scorm-package.component.html',
    styleUrls: ['./slide-scorm-package.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SlideScormPackageComponent implements OnInit {
  @ViewChild(IframeFullscreenComponent) public fullscreenComponent: IframeFullscreenComponent;
  @Input() public slideCompleted: boolean;
  @Input() public id: string;
  @Input() public set document(value: FileModel) {
    this._document = value;

    if (value) {
      this.origin = new URL(value.uri).origin;
      this.scormEmbedUrl = this.scormApiService.getScormPackageEmbedUrl(value);
    }
  }

  public get document() {
    return this._document;
  }

  @Input() public isQuiz = false;

  @ViewChild(IframeFullscreenComponent, {static: false}) iframeComponent: IframeFullscreenComponent;  

  public scormScore: ScormPackageResults = {
    min: 0,
    max: 100,
    status: ScormApiService.INCOMPLETE_SCORM_STATUS,
    raw: 0,
    interactions: []
  };

  public scormEmbedUrl: string;

  public scormProgressLoaded = false;
  public isDownloading = false;
  public enableDownload = false;
  public enableFullscreen = false;

  private unsubscribe: Subject<void> = new Subject();
  private currentSuspendData = null;
  private _document: FileModel;

  private origin: string = '*';

  private userDataInitialized = false;

  constructor(
    private readonly mediaPresignApiService: MediaPresignApiService,
    private readonly downloadService: DownloadService,
    private readonly profileService: ProfileService,
    private readonly settingsService: SettingsService,
    private readonly scormApiService: ScormApiService) { }

  ngOnInit(): void {
    this.loadScormProgress();

    this.settingsService.getSettings()
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe(data => {
        const { scormFullscreenBehaviour } = data.lesson;
        this.enableFullscreen = data.lesson.scormFullscreenBehaviour !== FullScreenBehaviourType.DISABLED;
        this.enableDownload = data.lesson.scormEnableDownload;

        if (scormFullscreenBehaviour === FullScreenBehaviourType.AUTOMATICALLY) {
          setTimeout(() => {
            this.iframeComponent?.fullscreenComponent?.fullScreenModelToggle();
          }, 500);
        }
      });
  }

  ngOnDestroy(): void {
    this.scormApiService.packageInProgress.next(null);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public downloadFile(): void {
    this.isDownloading = true;

    this.mediaPresignApiService.getPreSignedUrl(EntityTypeId.PARAGRAPH, BundleType.SLIDE_SCORM, FieldName.AWS_FILE, this.document)
      .pipe(
        mergeMap(url => this.downloadService.downloadFile(url, this.document.provider, this.document.filename, false, false)),
        finalize(() => this.isDownloading = false)
      )
      .subscribe();
  }

  public sendSuspendDataPostMessage(data = this.currentSuspendData): void {
    if (!data) {
      return;
    }
  
    const iframe = this.iframeComponent?.iframe;
    if (!iframe) {
      return;
    }
  
    iframe.nativeElement.contentWindow.postMessage({ name: 'cmi.suspend_data', value: data }, this.origin);
  }

  @HostListener('window:message', ['$event'])
  public onMessage(event: { data: { name: string, value: string }}) {
    const { name, value } = event.data;

    if (!name) {
      return;
    }

    switch (name) {
      case 'scorm.loaded': {
        if (!this.userDataInitialized) {
          this.setScormUserData();
          this.sendSuspendDataPostMessage();

          this.userDataInitialized = true;
        }

        break;
      }
      case 'cmi.suspend_data': {
        if (!this.isQuiz) {
          this.scormApiService.saveScormProgress(this.id, value).subscribe();
        }
        this.currentSuspendData = value;

        break;
      }
      case 'cmi.core.lesson_status': {
        const isCompleted = value !== ScormApiService.INCOMPLETE_SCORM_STATUS;
        this.scormScore.status = value;

        this.scormStatusUpdated(isCompleted);
        break;
      }
      case 'cmi.completion_status':
      case 'cmi.success_status': {
        if (name === 'cmi.completion_status' && this.isQuiz) {
          return;
        }

        const isCompleted = value !== ScormApiService.INCOMPLETE_SCORM2004_STATUS && value !== ScormApiService.INCOMPLETE_SCORM_STATUS;
        this.scormScore.status = isCompleted ? value : ScormApiService.INCOMPLETE_SCORM_STATUS;
        this.scormStatusUpdated(isCompleted);

        break;
      }
      case 'cmi.core.score.min': {
        this.scormScore.min = +value;
        break;
      }
      case 'cmi.core.score.max': {
        this.scormScore.max = +value;
        break;
      }
      case 'cmi.core.score.raw': {
        this.scormScore.raw = +value;
        break;
      }
      case (name && name.match(/cmi.interactions.[0-9]+./) || {}).input: {
        const interactions = this.scormScore.interactions;
        const propertyName = name.replace(/cmi.interactions.[0-9]+./, '');

        if (propertyName === 'id') {
          interactions.push({});
        }


        let lastInteraction = interactions[interactions.length - 1];
        if (lastInteraction) {
          Object.assign(lastInteraction, { [propertyName]: value })
        }
        
        break;
      }
    }

    this.scormDataChanged();
  }

  public loadScormProgress(): void {
    if (this.isQuiz) {
      this.scormProgressLoaded = true;
      return;
    }

    this.scormApiService.getScormProgress(this.id)  
      .pipe(
        finalize(() => {
          this.scormProgressLoaded = true;
        })
      )
      .subscribe(data => {
        this.currentSuspendData = data;
        this.sendSuspendDataPostMessage(data);
      });
  }

  public setScormUserData(): void {
    const iframe = this.iframeComponent?.iframe;
    if (!iframe) {
      return;
    }

    const currentUser = this.profileService.getCurrentProfileValue();
    if (iframe && iframe.nativeElement && iframe.nativeElement.contentWindow) {
      iframe.nativeElement.contentWindow.postMessage({ name: 'cmi.core.student_id', value: currentUser.id }, this.origin);
      iframe.nativeElement.contentWindow.postMessage({ name: 'cmi.core.student_name', value: currentUser.formattedProfileName }, this.origin);

      const currentStatus = this.slideCompleted ? ScormStatus.COMPLETED : ScormStatus.NOT_ATTEMPTED;

      iframe.nativeElement.contentWindow.postMessage({ name: 'cmi.core.lesson_status', value: currentStatus }, this.origin);
    }
  }

  private scormDataChanged(): void {
    this.scormApiService.packageInProgress.next(this.scormScore);
  }

  private scormStatusUpdated(isCompleted: boolean): void {
    if (isCompleted) {
      this.fullscreenComponent?.fullscreenComponent?.exitFromFullScreenMode();
      return;
    }

    this.scormScore.interactions = [];
  }
}
